import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Clock, MapPin, ExternalLink, ChevronLeft, ChevronRight, Calendar as CalIcon, X, Star } from "lucide-react";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const CATEGORY_COLOR = {
  Service:   "bg-[var(--blue)]/15 text-[var(--blue-deep)] border-[var(--blue)]/30",
  Education: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Social:    "bg-amber-100 text-amber-800 border-amber-200",
  Holiday:   "bg-rose-100 text-rose-800 border-rose-200",
  Community: "bg-[var(--honey)]/25 text-[var(--blue-ink)] border-[var(--honey)]/50",
  Jewish:    "bg-purple-100 text-purple-800 border-purple-200",
};

/** Local YYYY-MM-DD from a Date (no UTC shift). */
function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Build the 6x7 grid for a month. Includes leading/trailing days from adjacent months. */
function monthGrid(year, monthIdx) {
  const first = new Date(year, monthIdx, 1);
  const startWeekday = first.getDay();
  const start = new Date(year, monthIdx, 1 - startWeekday);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export default function CalendarMonthly() {
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIso, setSelectedIso] = useState(null);

  const year = cursor.getFullYear();
  const monthIdx = cursor.getMonth();
  const days = useMemo(() => monthGrid(year, monthIdx), [year, monthIdx]);
  const gridStart = days[0];
  const gridEnd = days[41];

  // Fetch DB events + recurring once
  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/events`).catch(() => ({ data: [] })),
      axios.get(`${API}/weekly/recurring`).catch(() => ({ data: [] })),
    ]).then(([ev, rec]) => {
      if (!alive) return;
      setEvents(Array.isArray(ev.data) ? ev.data : []);
      setRecurring(Array.isArray(rec.data) ? rec.data : []);
    });
    return () => { alive = false; };
  }, []);

  // Refetch overrides + Hebcal holidays whenever the visible grid changes
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const startIso = isoLocal(gridStart);
    const endIso = isoLocal(gridEnd);
    const hebcalUrl = `https://www.hebcal.com/hebcal?v=1&cfg=json&maj=on&min=on&mod=on&mf=on&ss=on&i=off&start=${startIso}&end=${endIso}`;
    Promise.all([
      axios.get(`${API}/weekly/overrides`, {
        params: { start_date: startIso, end_date: endIso },
      }).catch(() => ({ data: [] })),
      axios.get(hebcalUrl).catch(() => ({ data: { items: [] } })),
    ]).then(([ov, hc]) => {
      if (!alive) return;
      setOverrides(Array.isArray(ov.data) ? ov.data : []);
      const items = Array.isArray(hc.data?.items) ? hc.data.items : [];
      // Only holidays (skip candle-lighting / havdalah / parashat entries even if returned).
      setHolidays(items.filter((h) => (h.category || "").toLowerCase() === "holiday"));
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [gridStart, gridEnd]);

  // Combine every source into a Map<isoDate, event[]>
  const byDate = useMemo(() => {
    const map = new Map();
    for (const d of days) map.set(isoLocal(d), []);

    const push = (iso, item) => {
      if (!map.has(iso)) return;
      const bucket = map.get(iso);
      const dup = bucket.some(
        (x) => (x.title || "").toLowerCase().trim() === (item.title || "").toLowerCase().trim()
      );
      if (!dup) bucket.push(item);
    };

    // 1. DB events (real dated events)
    for (const e of events) {
      if (!e.start_date) continue;
      push(e.start_date, {
        title: e.title,
        time: e.start_time,
        end_time: e.end_time,
        location: e.location,
        description: e.description,
        category: e.category || "Community",
        rsvp_url: e.rsvp_url,
        details_url: e.details_url,
        featured: !!e.featured,
        source: "event",
      });
    }

    // 2. Recurring items - expand to every matching weekday in the visible grid
    for (const d of days) {
      const iso = isoLocal(d);
      const wd = d.getDay(); // 0..6
      for (const r of recurring) {
        if (r.active === false) continue;
        if (r.weekday !== wd) continue;
        push(iso, {
          title: r.title,
          time: r.time,
          location: r.location,
          description: r.description,
          category: r.category || "Service",
          rsvp_url: r.rsvp_url,
          details_url: r.details_url,
          source: "recurring",
        });
      }
    }

    // 3. One-off weekly overrides (specific-date entries added by admin)
    for (const o of overrides) {
      if (!o.date) continue;
      push(o.date, {
        title: o.override_title || o.title,
        time: o.time,
        location: o.location,
        description: o.description,
        category: o.category || "Community",
        rsvp_url: o.rsvp_url,
        details_url: o.details_url,
        source: "override",
      });
    }

    // 4. Jewish holidays from Hebcal
    for (const h of holidays) {
      push(h.date, {
        title: h.title,
        category: "Jewish",
        description: h.hebrew ? `${h.hebrew} · ${h.hdate || ""}`.trim() : (h.hdate || ""),
        details_url: h.link,
        subcat: h.subcat,
        source: "hebcal",
      });
    }

    // Sort each day by time (untimed items last)
    for (const [k, arr] of map) {
      arr.sort((a, b) => {
        const ta = a.time || "";
        const tb = b.time || "";
        if (!ta && tb) return 1;
        if (ta && !tb) return -1;
        return ta.localeCompare(tb);
      });
      map.set(k, arr);
    }
    return map;
  }, [days, events, recurring, overrides, holidays]);

  const shift = (delta) => setCursor(new Date(year, monthIdx + delta, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelectedIso(isoLocal(t));
  };

  const monthEventCount = useMemo(() => {
    let n = 0;
    for (const [k, arr] of byDate) {
      const [y, m] = k.split("-").map(Number);
      if (y === year && m === monthIdx + 1) n += arr.length;
    }
    return n;
  }, [byDate, year, monthIdx]);

  const selectedEvents = selectedIso ? byDate.get(selectedIso) || [] : [];
  const todayIso = isoLocal(today);

  return (
    <>
      <SEOHead
        title="Temple Calendar"
        description="Monthly calendar for Temple Israel of Brevard - services, classes, community events, and Jewish holidays. Click any date to see the full day&rsquo;s schedule."
        path="/calendar/month"
      />
      <PageHero
        eyebrow="Temple Calendar"
        title="Everything happening this month."
        subtitle={`${MONTHS[monthIdx]} ${year} · ${monthEventCount} event${monthEventCount === 1 ? "" : "s"} · includes Jewish holidays`}
      />

      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          {/* Nav */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-testid="monthly-nav">
            <div className="flex items-center gap-2">
              <button onClick={() => shift(-1)} className="btn-ghost text-sm" data-testid="monthly-prev" aria-label="Previous month">
                <ChevronLeft size={16} /> Prev
              </button>
              <button onClick={goToday} className="btn-ghost text-sm" data-testid="monthly-today">
                Today
              </button>
              <button onClick={() => shift(1)} className="btn-ghost text-sm" data-testid="monthly-next" aria-label="Next month">
                Next <ChevronRight size={16} />
              </button>
              <h2 className="ml-3 font-display text-2xl md:text-3xl text-[var(--blue-ink)]" data-testid="monthly-title">
                {MONTHS[monthIdx]} {year}
              </h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/calendar/weekly" className="btn-ghost text-sm">
                <CalIcon size={14} /> Weekly view
              </Link>
              <Link to="/events" className="btn-ghost text-sm">
                Featured events
              </Link>
            </div>
          </div>

          {loading ? (
            <p className="text-center py-16 text-[var(--muted)]">Loading calendar…</p>
          ) : (
            <>
              {/* Weekday header */}
              <div className="grid grid-cols-7 gap-px bg-[var(--line)] rounded-t-xl overflow-hidden mb-px">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="bg-[var(--cream)] text-[var(--blue-ink)] font-semibold text-xs md:text-sm uppercase tracking-wider text-center py-2">
                    {w}
                  </div>
                ))}
              </div>

              {/* Grid */}
              <div className="grid grid-cols-7 gap-px bg-[var(--line)] rounded-b-xl overflow-hidden">
                {days.map((d, i) => {
                  const iso = isoLocal(d);
                  const inMonth = d.getMonth() === monthIdx;
                  const isToday = iso === todayIso;
                  const isSelected = iso === selectedIso;
                  const dayEvents = byDate.get(iso) || [];
                  const hasMajorHoliday = dayEvents.some((e) => e.source === "hebcal" && (e.subcat === "major" || e.subcat === "fast"));
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedIso(iso)}
                      data-testid={`monthly-day-${iso}`}
                      className={`text-left min-h-[104px] md:min-h-[128px] p-1.5 md:p-2 flex flex-col gap-1 transition-colors hover:bg-[var(--cream)]/60 focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/60 ${
                        hasMajorHoliday ? "bg-purple-50" : "bg-white"
                      } ${inMonth ? "" : "opacity-40"} ${isSelected ? "ring-2 ring-[var(--blue)] ring-inset" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full text-xs md:text-sm ${
                            isToday
                              ? "bg-[var(--blue)] text-white font-semibold"
                              : "text-[var(--ink)]"
                          }`}
                        >
                          {d.getDate()}
                        </span>
                        {dayEvents.length > 0 && (
                          <span className="text-[10px] font-semibold text-[var(--muted)] md:hidden">
                            {dayEvents.length}
                          </span>
                        )}
                      </div>
                      <div className="hidden md:flex flex-col gap-0.5 overflow-hidden">
                        {dayEvents.slice(0, 4).map((e, idx) => (
                          <span
                            key={idx}
                            title={`${e.time ? e.time + " " : ""}${e.title}`}
                            className={`text-[11px] leading-tight px-1.5 py-0.5 rounded border truncate flex items-center gap-1 ${CATEGORY_COLOR[e.category] || CATEGORY_COLOR.Community}`}
                          >
                            {e.featured && <Star size={9} className="flex-shrink-0" />}
                            <span className="truncate">
                              {e.time ? `${e.time} ` : ""}{e.title}
                            </span>
                          </span>
                        ))}
                        {dayEvents.length > 4 && (
                          <span className="text-[10px] text-[var(--muted)] mt-0.5">
                            +{dayEvents.length - 4} more
                          </span>
                        )}
                      </div>
                      {/* Mobile dots */}
                      <div className="md:hidden flex gap-0.5 mt-auto flex-wrap">
                        {dayEvents.slice(0, 5).map((e, idx) => (
                          <span
                            key={idx}
                            className={`w-1.5 h-1.5 rounded-full ${
                              e.category === "Service"   ? "bg-[var(--blue)]" :
                              e.category === "Education" ? "bg-emerald-500" :
                              e.category === "Social"    ? "bg-amber-500" :
                              e.category === "Holiday"   ? "bg-rose-500" :
                              e.category === "Jewish"    ? "bg-purple-500" :
                                                           "bg-[var(--honey)]"
                            }`}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-[var(--muted)]" data-testid="monthly-legend">
                {["Service", "Education", "Social", "Holiday", "Community", "Jewish"].map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-sm border ${CATEGORY_COLOR[c]}`} />
                    {c === "Jewish" ? "Jewish holiday" : c}
                  </span>
                ))}
              </div>

              {/* Selected day details */}
              {selectedIso && (
                <div className="mt-8 card-soft p-5 md:p-7 bg-white" data-testid="monthly-day-details">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <h3 className="font-display text-xl md:text-2xl text-[var(--blue-ink)]">
                      {new Date(selectedIso + "T12:00:00").toLocaleDateString(undefined, {
                        weekday: "long", month: "long", day: "numeric", year: "numeric",
                      })}
                    </h3>
                    <button
                      onClick={() => setSelectedIso(null)}
                      className="text-[var(--muted)] hover:text-[var(--ink)] p-1"
                      aria-label="Close day details"
                      data-testid="monthly-close-details"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  {selectedEvents.length === 0 ? (
                    <p className="text-[var(--muted)] text-sm">Nothing scheduled on this day.</p>
                  ) : (
                    <ul className="space-y-4">
                      {selectedEvents.map((e, idx) => (
                        <li key={idx} className={`border-l-4 pl-4 ${e.category === "Jewish" ? "border-purple-400" : "border-[var(--honey)]"}`}>
                          <div className="flex flex-wrap items-baseline gap-2 mb-1">
                            <h4 className="font-semibold text-[var(--blue-ink)] text-lg">{e.title}</h4>
                            {e.category && (
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${CATEGORY_COLOR[e.category] || CATEGORY_COLOR.Community}`}>
                                {e.category === "Jewish" ? "Jewish holiday" : e.category}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-[var(--ink-soft)] mb-2">
                            {e.time && (
                              <span className="inline-flex items-center gap-1"><Clock size={13} /> {e.time}{e.end_time ? ` – ${e.end_time}` : ""}</span>
                            )}
                            {e.location && (
                              <span className="inline-flex items-center gap-1"><MapPin size={13} /> {e.location}</span>
                            )}
                          </div>
                          {e.description && (
                            <p className="text-sm text-[var(--ink)] whitespace-pre-wrap mb-2">{e.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {e.rsvp_url && (
                              <a href={e.rsvp_url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">
                                RSVP <ExternalLink size={12} />
                              </a>
                            )}
                            {e.details_url && e.details_url !== e.rsvp_url && (
                              <a href={e.details_url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">
                                Details <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}

          <p className="text-center mt-12 text-xs text-[var(--muted)]">
            Jewish holidays via <a href="https://www.hebcal.com" target="_blank" rel="noopener noreferrer" className="text-[var(--blue-deep)] underline">hebcal.com</a>.
            To submit an event, email{" "}
            <a href="mailto:tioffice@tiofbrevard.com" className="text-[var(--blue-deep)] underline">
              tioffice@tiofbrevard.com
            </a>.
          </p>
        </div>
      </section>
    </>
  );
}
